import { useState } from 'react';
import { cn } from '@/lib/cn';
import { Button } from '@/ui/Button';
import { Drawer } from '@/ui/Drawer';
import { Field, Input, Select, Textarea } from '@/ui/Field';
import { SelectorDeIcono } from '@/components/SelectorDeIcono';
import { useAreasDeGestion } from '../api';
import {
  pedirGestionAlAbrir, useCrearRuta, useEditarRuta, useEmpresas,
  type CuerpoDeRuta, type RutaDetalle,
} from './api';

/**
 * FormularioDeRuta — el alta y la edición de los datos de una ruta.
 *
 * ## Quitar un filtro no hacía nada
 *
 * Elegir «Cualquier área» mandaba `area_id: null`, y el servidor lee `null` como
 * «no lo toques»: el área seguía puesta y la respuesta era «Ruta actualizada».
 * Lo mismo con el rol, el cargo, la empresa y la descripción. En la edición, el
 * vacío va ahora como lo entiende el servidor —`0` o `""`— (ver `api.ts`).
 *
 * ## …y no se puede quitar uno sin querer
 *
 * El vanilla pedía las áreas y las empresas con un `catch` que devolvía una
 * lista vacía: si fallaban, el desplegable solo ofrecía «Cualquier área». Con el
 * `null` de antes eso no borraba nada, por casualidad. Con el vacío que ahora sí
 * borra, un fallo de carga borraría el filtro al guardar. Así que si una lista
 * no se pudo leer, se dice, y **ese campo no viaja**: el servidor lo deja como
 * estaba. Y si la ruta apunta a un área o una empresa que ya no está activa,
 * sigue saliendo como opción en vez de desaparecer del desplegable.
 *
 * ## «Rol destino» ofrece tres roles
 *
 * La misma limitación que «Por rol» en las asignaciones de un curso: la lista
 * real sale de `GET /admin/roles`, que es solo de administración, y «Auditor» no
 * se puede elegir. Queda como decisión de contrato en el índice del rediseño.
 */

const ROLES: Array<[string, string]> = [
  ['colaborador', 'Colaboradores'], ['coordinador', 'Coordinadores'], ['admin', 'Administradores'],
];

const PUBLICACION = [
  {
    valor: 'publicado' as const,
    titulo: 'Publicar ahora',
    icono: 'bi-broadcast',
    ayuda: 'El equipo la ve en cuanto se crea, aunque todavía no tenga cursos.',
  },
  {
    valor: 'borrador' as const,
    titulo: 'Guardar como borrador',
    icono: 'bi-pencil-square',
    ayuda: 'Solo la ve gestión. Se publica desde «Gestionar ruta» cuando tenga sus cursos.',
  },
];

/** El alta, en su panel. Al crearla va a su detalle con la gestión abierta:
 *  una ruta recién creada no tiene cursos, y añadirlos es lo siguiente. */
export function NuevaRuta({ abierta, onCerrar }: { abierta: boolean; onCerrar: () => void }) {
  return (
    <Drawer
      abierto={abierta}
      onCambio={(v) => { if (!v) onCerrar(); }}
      titulo="Nueva ruta de aprendizaje"
      descripcion="Agrupa cursos en un plan que se hace en orden."
      ancho="md"
    >
      {/* Se monta al abrir y se desmonta al cerrar: otra alta empieza vacía. */}
      {abierta && <FormularioDeRuta onHecho={onCerrar} />}
    </Drawer>
  );
}

export function FormularioDeRuta({
  ruta, onHecho,
}: {
  /** Sin ruta es un alta. */
  ruta?: RutaDetalle;
  onHecho?: () => void;
}) {
  const edicion = !!ruta;
  const crear = useCrearRuta();
  const editar = useEditarRuta();
  const areas = useAreasDeGestion(true);
  const empresas = useEmpresas(true);

  const [titulo, setTitulo] = useState(ruta?.title ?? '');
  const [descripcion, setDescripcion] = useState(ruta?.description ?? '');
  const [area, setArea] = useState(ruta?.area_id ? String(ruta.area_id) : '');
  const [rol, setRol] = useState(ruta?.target_role ?? '');
  const [cargo, setCargo] = useState(ruta?.target_position ?? '');
  const [empresa, setEmpresa] = useState(ruta?.company_id ? String(ruta.company_id) : '');
  const [icono, setIcono] = useState(ruta?.cover_icon ?? '');
  const [color, setColor] = useState(ruta?.cover_icon_color || '#0D2B4E');
  const [secuencial, setSecuencial] = useState(ruta ? ruta.sequential : true);
  const [obligatoria, setObligatoria] = useState(ruta?.is_mandatory ?? false);
  const [publicacion, setPublicacion] = useState<'publicado' | 'borrador'>('publicado');
  const [errorDeTitulo, setErrorDeTitulo] = useState('');

  const pref = edicion ? `er-${ruta!.id}` : 'nr';
  // Un área o una empresa que ya no están activas no salen en la lista; sin
  // esto, el desplegable diría «Cualquiera» y guardar la borraría.
  const areaHuerfana = ruta?.area_id && areas.data && !areas.data.some((a) => a.id === ruta.area_id);
  const empresaHuerfana =
    ruta?.company_id && empresas.data && !empresas.data.some((e) => e.id === ruta.company_id);
  const rolFueraDeLista = rol && !ROLES.some(([k]) => k === rol);

  function enviar(e: React.FormEvent) {
    e.preventDefault();
    if (!titulo.trim()) {
      setErrorDeTitulo('La ruta necesita un título.');
      return;
    }

    if (!edicion) {
      const cuerpo: CuerpoDeRuta = {
        title: titulo.trim(),
        description: descripcion.trim() || null,
        area_id: area ? Number(area) : null,
        target_role: rol || null,
        target_position: cargo.trim() || null,
        company_id: empresa ? Number(empresa) : null,
        cover_icon: icono || null,
        cover_icon_color: color,
        sequential: secuencial,
        is_mandatory: obligatoria,
        status: publicacion,
      };
      crear.mutate(cuerpo, {
        onSuccess: ({ id }) => {
          Utils.toast(
            publicacion === 'publicado' ? 'Ruta creada: ahora añade sus cursos' : 'Ruta guardada como borrador: ahora añade sus cursos',
            'success',
          );
          onHecho?.();
          pedirGestionAlAbrir(id);
          location.hash = `#rutas/${id}`;
        },
        onError: (err) => Utils.toast(err instanceof Error ? err.message : 'No se pudo crear la ruta', 'error'),
      });
      return;
    }

    // Edición: el vacío que el servidor entiende —`0`, `""`— y NO `null`, que
    // para él es «no lo toques». Las listas que no se pudieron leer no viajan.
    const cuerpo: Partial<CuerpoDeRuta> = {
      title: titulo.trim(),
      description: descripcion.trim(),
      target_role: rol,
      target_position: cargo.trim(),
      cover_icon_color: color,
      sequential: secuencial,
      is_mandatory: obligatoria,
      ...(icono ? { cover_icon: icono } : {}),
      ...(areas.isSuccess ? { area_id: area ? Number(area) : 0 } : {}),
      ...(empresas.isSuccess ? { company_id: empresa ? Number(empresa) : 0 } : {}),
    };
    editar.mutate(
      { id: ruta!.id, cuerpo },
      {
        onSuccess: () => {
          Utils.toast('Ruta actualizada', 'success');
          onHecho?.();
        },
        onError: (err) => Utils.toast(err instanceof Error ? err.message : 'No se pudo guardar', 'error'),
      },
    );
  }

  return (
    <form onSubmit={enviar} className="flex flex-col gap-4" noValidate>
      <Field label="Título" error={errorDeTitulo} id={`${pref}-titulo`}>
        <Input
          value={titulo}
          maxLength={200}
          onChange={(e) => { setTitulo(e.target.value); setErrorDeTitulo(''); }}
          placeholder="Ej: Ruta de inducción para asesores"
        />
      </Field>

      <Field label="Descripción" opcional id={`${pref}-descripcion`}>
        <Textarea
          rows={2}
          value={descripcion}
          onChange={(e) => setDescripcion(e.target.value)}
          placeholder="¿Qué logra quien la completa?"
        />
      </Field>

      <fieldset className="flex flex-col gap-3 rounded-sm border border-line p-3">
        <legend className="px-1 text-2 font-semibold text-ink-2">A quién va dirigida</legend>
        <p className="text-2 text-muted">
          Se combinan: la ruta es para quien coincida con todos los que dejes puestos. Para elegir a
          personas concretas, usa «Personas» en la gestión de la ruta; eso sustituye a estos filtros.
        </p>
        <div className="grid gap-3 sm:grid-cols-2">
          <Field label="Área" id={`${pref}-area`} error={areas.isError ? 'No se pudieron cargar las áreas: al guardar, el área no cambia.' : undefined}>
            <Select value={area} onChange={(e) => setArea(e.target.value)} disabled={!areas.isSuccess}>
              <option value="">{areas.isLoading ? 'Cargando…' : '— Cualquier área —'}</option>
              {(areas.data ?? []).map((a) => <option key={a.id} value={a.id}>{a.name}</option>)}
              {areaHuerfana && (
                <option value={ruta!.area_id!}>{ruta!.area_name ?? `Área ${ruta!.area_id}`} (inactiva)</option>
              )}
            </Select>
          </Field>
          <Field label="Rol" id={`${pref}-rol`}>
            <Select value={rol} onChange={(e) => setRol(e.target.value)}>
              <option value="">— Cualquier rol —</option>
              {ROLES.map(([k, v]) => <option key={k} value={k}>{v}</option>)}
              {rolFueraDeLista && <option value={rol}>{ruta?.target_role_label ?? rol}</option>}
            </Select>
          </Field>
          <Field label="Cargo" opcional id={`${pref}-cargo`} ayuda="Tal como está en el perfil de cada persona.">
            <Input value={cargo} onChange={(e) => setCargo(e.target.value)} placeholder="Ej: Asesor comercial" />
          </Field>
          <Field label="Empresa" id={`${pref}-empresa`} error={empresas.isError ? 'No se pudieron cargar las empresas: al guardar, la empresa no cambia.' : undefined}>
            <Select value={empresa} onChange={(e) => setEmpresa(e.target.value)} disabled={!empresas.isSuccess}>
              <option value="">{empresas.isLoading ? 'Cargando…' : '— Cualquier empresa —'}</option>
              {(empresas.data ?? []).map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
              {empresaHuerfana && (
                <option value={ruta!.company_id!}>{ruta!.company_name ?? `Empresa ${ruta!.company_id}`} (inactiva)</option>
              )}
            </Select>
          </Field>
        </div>
      </fieldset>

      <div className="grid gap-4 sm:grid-cols-[1fr_auto]">
        <SelectorDeIcono id={`${pref}-icono`} valor={icono} onCambio={setIcono} respaldo="bi-signpost-split" />
        <Field label="Color" id={`${pref}-color`}>
          {/* Nativo, así que no lee el id del contexto de `Field`: se le pone a
              mano para que la etiqueta lo nombre y al pulsarla lo enfoque. */}
          <input
            id={`${pref}-color`}
            type="color"
            value={color}
            onChange={(e) => setColor(e.target.value)}
            className="h-11 w-16 cursor-pointer rounded-sm border border-field-border bg-surface p-1"
          />
        </Field>
      </div>

      <div className="flex flex-col gap-1">
        <label className="flex min-h-11 cursor-pointer items-center gap-2 text-3 text-ink-2">
          <input
            type="checkbox"
            checked={secuencial}
            onChange={(e) => setSecuencial(e.target.checked)}
            className="size-4 accent-brand"
          />
          En orden: cada curso se abre al terminar los requeridos anteriores
        </label>
        <label className="flex min-h-11 cursor-pointer items-center gap-2 text-3 text-ink-2">
          <input
            type="checkbox"
            checked={obligatoria}
            onChange={(e) => setObligatoria(e.target.checked)}
            className="size-4 accent-brand"
          />
          Ruta obligatoria
        </label>
      </div>

      {!edicion && (
        <fieldset>
          <legend className="mb-1.5 text-2 font-semibold text-ink-2">Publicación</legend>
          <div className="grid gap-2 sm:grid-cols-2">
            {PUBLICACION.map((o) => (
              <label
                key={o.valor}
                className={cn(
                  'flex cursor-pointer gap-2.5 rounded-sm border p-3 text-3',
                  publicacion === o.valor ? 'border-brand-ink bg-brand/[.06]' : 'border-line hover:border-brand-ink',
                )}
              >
                <input
                  type="radio"
                  name="nr-publicacion"
                  value={o.valor}
                  checked={publicacion === o.valor}
                  onChange={() => setPublicacion(o.valor)}
                  className="mt-0.5 size-4 accent-brand"
                />
                <span>
                  <span className="flex items-center gap-1.5 font-semibold text-ink">
                    <i className={cn('bi', o.icono)} aria-hidden="true" /> {o.titulo}
                  </span>
                  <span className="mt-0.5 block text-2 text-muted">{o.ayuda}</span>
                </span>
              </label>
            ))}
          </div>
        </fieldset>
      )}

      <div className="flex justify-end gap-2">
        {onHecho && !edicion && (
          <Button type="button" variant="ghost" onClick={onHecho}>Cancelar</Button>
        )}
        <Button type="submit" loading={crear.isPending || editar.isPending}>
          <i className="bi bi-check-lg" aria-hidden="true" /> {edicion ? 'Guardar los datos' : 'Crear ruta'}
        </Button>
      </div>
    </form>
  );
}
